// ./src/components/features/Cars/CarUserAssignmentEditor/CarUserAssignmentEditor.tsx
import React, { useMemo, useCallback, useState } from "react";
import {
  Stack,
  Group,
  Text,
  Box,
  Paper,
  Avatar,
  Badge,
  ActionIcon,
  Select,
  Button,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { User, UserCarInput } from "@/utils/api";
import { AppIcon } from "@/components/generic";
import { UserSelector } from "@/components/features/Users";
import { USER_CAR_ROLES } from "@/config/userCarRoles";
import { buildFullName } from "@/utils";

// ============================================
// Types
// ============================================

export interface CarUserAssignment extends UserCarInput {
  /** User object for display (not sent to API) */
  user?: User;
}

export interface CarUserAssignmentEditorProps {
  /** Current assignments */
  value: CarUserAssignment[];

  /** Callback when assignments change */
  onChange: (assignments: CarUserAssignment[]) => void;

  /** Current user ID (for default owner and preventing self-removal warning) */
  currentUserId?: string | null;

  /** Current user object (for display when adding as default owner) */
  currentUser?: User | null;

  /** Whether the editor is disabled */
  disabled?: boolean;

  /** Error message to display */
  error?: string | null;

  /** Label for the section */
  label?: string;

  /** Whether at least one owner is required */
  requireOwner?: boolean;
}

// ============================================
// Role Options
// ============================================

interface RoleOption {
  value: string;
  label: string;
  color: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get user initials for avatar
 */
const getUserInitials = (user: User | undefined): string => {
  if (!user) return "??";

  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "??";
};

// ============================================
// Component
// ============================================

const CarUserAssignmentEditorComponent: React.FC<CarUserAssignmentEditorProps> = ({
  value,
  onChange,
  currentUserId,
  //currentUser,
  disabled = false,
  error,
  label,
  requireOwner = true,
}) => {
  const { t } = useTranslation(["cars", "common"]);

  // State for adding new user
  const [isAdding, setIsAdding] = useState(false);

  // Default label
  const defaultLabel = label ?? t("cars:form.sections.users");

  // Role options with translations
  const roleOptions = useMemo((): RoleOption[] => [
    {
      value: String(USER_CAR_ROLES.OWNER),
      label: t("cars:userCarRoles.owner"),
      color: "blue",
    },
    {
      value: String(USER_CAR_ROLES.DRIVER),
      label: t("cars:userCarRoles.driver"),
      color: "green",
    },
    {
      value: String(USER_CAR_ROLES.VIEWER),
      label: t("cars:userCarRoles.viewer"),
      color: "gray",
    },
  ], [t]);

  // Get role label and color
  const getRoleInfo = useCallback((roleId: number | null | undefined): { label: string; color: string } => {
    const option = roleOptions.find((opt) => opt.value === String(roleId));
    return option || { label: t("cars:userCarRoles.unknown"), color: "gray" };
  }, [roleOptions, t]);

  // Get list of already assigned user IDs (to exclude from selector)
  const assignedUserIds = useMemo(() => {
    return value
      .map((assignment) => assignment.userId)
      .filter((id): id is string => Boolean(id));
  }, [value]);

  // Check if we can remove a user (must keep at least one owner if required)
  const canRemoveUser = useCallback((userId: string | null | undefined): boolean => {
    if (!requireOwner) return true;

    const assignment = value.find((a) => a.userId === userId);
    if (!assignment || assignment.roleId !== USER_CAR_ROLES.OWNER) {
      return true; // Can always remove non-owners
    }

    // Count owners
    const ownerCount = value.filter((a) => a.roleId === USER_CAR_ROLES.OWNER).length;
    return ownerCount > 1;
  }, [value, requireOwner]);

  // Handle adding a new user
  const handleAddUser = useCallback((_name: string, userId: string | null, user?: User) => {
    if (!userId || !user) return;

    const newAssignment: CarUserAssignment = {
      userId,
      roleId: USER_CAR_ROLES.DRIVER, // Default to Driver role
      user,
    };

    onChange([...value, newAssignment]);
    setIsAdding(false);
  }, [value, onChange]);

  // Handle role change for a user
  const handleRoleChange = useCallback((userId: string, newRoleId: string | null) => {
    if (!newRoleId) return;

    const updatedAssignments = value.map((assignment) => {
      if (assignment.userId === userId) {
        return {
          ...assignment,
          roleId: parseInt(newRoleId, 10),
        };
      }
      return assignment;
    });

    onChange(updatedAssignments);
  }, [value, onChange]);

  // Handle removing a user
  const handleRemoveUser = useCallback((userId: string) => {
    if (!canRemoveUser(userId)) {
      // Could show a notification here
      return;
    }

    const updatedAssignments = value.filter((assignment) => assignment.userId !== userId);
    onChange(updatedAssignments);
  }, [value, onChange, canRemoveUser]);

  // Handle cancel adding
  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
  }, []);

  // Handle start adding
  const handleStartAdd = useCallback(() => {
    setIsAdding(true);
  }, []);

  // Render a single assignment row
  const renderAssignmentRow = useCallback((assignment: CarUserAssignment) => {
    const { userId, roleId, user } = assignment;
    //const roleInfo = getRoleInfo(roleId);
    const isCurrentUser = userId === currentUserId;
    const removable = canRemoveUser(userId);

    return (
      <Paper key={userId} p="sm" withBorder>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Avatar size={40} radius="xl" color="brand">
              {getUserInitials(user)}
            </Avatar>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" fw={500} lineClamp={1}>
                  {buildFullName(user)}
                </Text>
                {isCurrentUser && (
                  <Badge size="xs" variant="light" color="blue">
                    {t("cars:userAssignment.you")}
                  </Badge>
                )}
              </Group>
              {user?.email && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {user.email}
                </Text>
              )}
            </Box>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Select
              value={String(roleId)}
              onChange={(newValue) => userId && handleRoleChange(userId, newValue)}
              data={roleOptions}
              size="xs"
              w={110}
              disabled={disabled}
              allowDeselect={false}
              renderOption={({ option }) => {
                const opt = option as RoleOption;
                return (
                  <Group gap="xs">
                    <Badge size="xs" color={opt.color} variant="light">
                      {opt.label}
                    </Badge>
                  </Group>
                );
              }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              size="sm"
              onClick={() => userId && handleRemoveUser(userId)}
              disabled={disabled || !removable}
              title={
                removable
                  ? t("cars:userAssignment.remove")
                  : t("cars:userAssignment.cannotRemoveLastOwner")
              }
            >
              <AppIcon iconName="Trash" size={16} />
            </ActionIcon>
          </Group>
        </Group>
      </Paper>
    );
  }, [
    currentUserId,
    disabled,
    getRoleInfo,
    handleRemoveUser,
    handleRoleChange,
    roleOptions,
    canRemoveUser,
    t,
  ]);

  return (
    <Box>
      {defaultLabel && (
        <Text size="sm" fw={500} mb="xs">
          {defaultLabel}
        </Text>
      )}

      <Stack gap="sm">
        {/* Existing assignments */}
        {value.length > 0 ? (
          value.map(renderAssignmentRow)
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {t("cars:userAssignment.noUsers")}
          </Text>
        )}

        {/* Add user section */}
        {isAdding ? (
          <Paper p="sm" withBorder>
            <Stack gap="sm">
              <UserSelector
                name="newUserId"
                onChange={handleAddUser}
                placeholder={t("cars:userAssignment.selectUser")}
                userId={currentUserId}
                autoLoad
                // Filter out already assigned users - handled via userFilter
                userFilter={(user) => !assignedUserIds.includes(user.id || "")}
              />
              <Group justify="flex-end" gap="xs">
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={handleCancelAdd}
                  disabled={disabled}
                >
                  {t("common:buttons.cancel")}
                </Button>
              </Group>
            </Stack>
          </Paper>
        ) : (
          <Button
            variant="light"
            size="sm"
            leftSection={<AppIcon iconName="UserPlus" size={16} />}
            onClick={handleStartAdd}
            disabled={disabled}
          >
            {t("cars:userAssignment.addUser")}
          </Button>
        )}

        {/* Error message */}
        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export const CarUserAssignmentEditor = React.memo(CarUserAssignmentEditorComponent);